/**
 * @file Funding.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React, { useCallback, useEffect, useState } from "react";
import SkinPage from "../../SkinPage";
import Widget from "../../widgets/Widget";
import Request from "../../../../utils/fetchUtils";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { Accordion } from "rsuite";
import TextInput from "../../../../components/forms/TextInput";
import { PlaidLink } from "react-plaid-link";
import "./Funding.css";
import "react-tabs/style/react-tabs.css";
import Loader from "../../widgets/Loader";
import Swal from "sweetalert2";
import { getFromSession } from "../../../../utils/sessionUtils";
import { parseObjectDate } from "../../../../utils/dateUtils";
import { Checkbox, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import DgModal from "../../../../components/DgModal";
import DgButton from "../../../../components/DgButton";
import { useForm } from "../../../../utils/useForm";

function Funding() {
  const [achProfiles, setAchProfiles] = useState([]);
  const [openModalVerif, setOpenModalVerif] = useState(false);
  const [currVerifyAch, setCurrVerifyAch] = useState({});
  const [verifyData, setVerifyData, clearVerifyData] = useForm({
    amount1: 0,
    amount2: 0,
  });
  const [accounts, setAccounts] = useState(null);
  const [accountNickname, setaccountNickname] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAch, setSelectedAch] = useState({});
  const [plaidLink, setPlaidLink] = useState("");
  const [selProfileDepIx, setSelProfileDepIx] = useState(null);
  const [selProfileWithIx, setSelProfileWithIx] = useState(null);
  const [amountDeposit, setAmountDeposit] = useState("");
  const [amountWithdrawal, setAmountWithdrawal] = useState("");
  const [loading, setLoading] = useState(false);
  const brokerAccounts = getFromSession("brokerAccounts") ?? [];
  const tradierAccount = brokerAccounts.find(
    (acc) => acc.provider === "tradier"
  );
  const getPlaidLink = async () => {
    const res = await Request("trading/plaid/create-link", {}, "POST");
    if (res.link_token) {
      setPlaidLink(res.link_token);
    }
  };
  const createAchProfile = async () => {
    const accountData = accounts.accounts[selectedAccount];
    if (accountData) {
      if (accountNickname.length > 0) {
        setLoading(true);
        const res = await Request(
          "trading/plaid/exchange-token",
          {
            publicToken: accounts.publicToken,
            account: { ...accountData },
            nickname: accountNickname,
          },
          "POST"
        );
        setLoading(false);
        if (res) {
          Swal.fire("Success", "ACH profile created", "success").then(() => {
            setAccounts(null);
            setSelectedAccount(null);
            setaccountNickname("");
            fetchAch();
          });
        }
      } else {
        Swal.fire(
          "Error",
          "You need to write a nickname for this account",
          "warning"
        );
      }
    } else {
      Swal.fire("Error", "No account selected", "warning");
    }
  };
  const onPlaidSuccess = async (public_token, metadata) => {
    setAccounts({ ...metadata, publicToken: public_token });
    // Swal.fire("¡Great!", "Bank account successfully connected", "success").then(
    //   async () => {
    //     const res = await Request(
    //       "trading/plaid/exchange-token",
    //       {
    //         publicToken: public_token,
    //       },
    //       "POST"
    //     );
    //     if (res) {
    //       Swal.fire("Success", "ACH Profile created", "success");
    //     }
    //   }
    // );
  };
  const onPlaidExit = async (error, metadata) => {
    Swal.fire("Oh", "Connection aborted", "warning").then(() => {
      //setPlaidLink("");
    });
  };
  const fetchAch = useCallback(async () => {
    if (tradierAccount) {
      const res = await Request("trading/ach/" + tradierAccount.id);
      if (res.statusCode === 200) {
        setAchProfiles(res.body);
      }
    }
  }, []);
  const handleDeposit = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: `You are requesting a deposit of $ ${amountDeposit}`,
      icon: "question",
      showCancelButton: true,
    }).then(async ({ isConfirmed }) => {
      if (isConfirmed) {
        const profile = achProfiles[selProfileDepIx];
        if (profile) {
          if (profile.status === "APPROVED") {
            const res = await Request(
              "trading/transaction",
              {
                action: "DEPOSIT",
                accountId: tradierAccount.id,
                amount: amountDeposit,
                achProfile: profile.id,
              },
              "POST"
            );
            if (res.ok) {
              Swal.fire("Request successfully created", "", "success");
              setAmountDeposit("");
              setSelProfileDepIx(null);
            } else {
              Swal.fire(
                "Error requesting deposit",
                res.body?.error?.errors[0] ?? "Unknown tradier error",
                "error"
              );
            }
          } else {
            Swal.fire("Error", "Profile must be Approved to proceed", "error");
          }
        } else {
          Swal.fire("Error", "Please select a profile to proceed", "error");
        }
      }
    });
  };
  const handleWithdrawal = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: `You are requesting a withdrawal of $ ${amountWithdrawal}`,
      icon: "question",
      showCancelButton: true,
    }).then(async ({ isConfirmed }) => {
      if (isConfirmed) {
        const profile = achProfiles[selProfileWithIx];
        if (profile) {
          if (profile.status === "APPROVED") {
            const res = await Request(
              "trading/transaction",
              {
                action: "WITHDRAWAL",
                accountId: tradierAccount.id,
                amount: amountWithdrawal,
                achProfile: profile.id,
              },
              "POST"
            );
            if (res.ok) {
              setAmountWithdrawal("");
              setSelProfileWithIx(null);
            } else {
              Swal.fire(
                "Error requesting withdrawal",
                res.body?.error?.errors[0] ?? "Unknown tradier error",
                "error"
              );
            }
          } else {
            Swal.fire("Error", "Profile must be Approved to proceed", "error");
          }
        } else {
          Swal.fire("Error", "Please select a profile to proceed", "error");
        }
      }
    });
  };
  const handleCancelAch = (achData) => async () => {
    const { id } = achData;
    const comment = "";
    Swal.fire({
      title: "Are you sure?",
      text: "You will cancel this profile connection",
      icon: "warning",
      showCancelButton: true,
    }).then(async ({ isConfirmed }) => {
      if (isConfirmed) {
        const res = await Request(
          `trading/ach/${tradierAccount.id}/profile/${id}?comment=${comment}`,
          {},
          "DELETE"
        );
        if (res.ok) {
          Swal.fire("Success", "Profile successfully cancelled", "success");
        }
      }
    });
  };
  const handleVerifylAch = async () => {
    const { amount1, amount2 } = verifyData;
    const { id } = currVerifyAch;
    if (amount1 > 0 && amount2 > 0) {
      const res = await Request(
        `trading/ach/${tradierAccount.id}/profile/${id}`,
        {
          amount1,
          amount2,
        },
        "PATCH"
      );
      if (res.ok) {
        clearVerifyData();
        setCurrVerifyAch({});
        Swal.fire("Success", "Profile successfully verified", "success");
      }
    } else {
      Swal.fire("Error", "Please type valid micro-deposit amounts", "warning");
    }
  };
  const renderAchActions = (achData) => {
    const { status } = achData;
    const baseStyle = { color: "#95b2c5" };
    const cancelButton = (
      <IconButton onClick={handleCancelAch(achData)} aria-label="delete">
        <CloseIcon style={{ ...baseStyle }} />
      </IconButton>
    );
    const verifyButton = (
      <IconButton
        onClick={() => {
          setCurrVerifyAch(achData);
          setOpenModalVerif(true);
        }}
        aria-label="delete"
      >
        <PriceCheckIcon style={{ ...baseStyle }} />
      </IconButton>
    );
    switch (status) {
      case "CANCELED":
        return null;
      case "REJECTED":
        return null;
      case "SUBMITTED":
        return (
          <>
            {verifyButton}
            {cancelButton}
          </>
        );
      case "APPROVED":
        return <>{cancelButton}</>;
    }
  };

  const renderAchTable = (isWithdrawal = false) => {
    return (
      <table className="basic-table">
        <thead>
          <tr>
            <th colSpan={7}>ACH Profiles</th>
          </tr>
          <tr>
            <th width="5%"></th>
            <th width="15%">Status</th>
            <th width="15%">Nickname</th>
            <th width="18%">Bank Account Type</th>
            <th width="25%">Bank Account</th>
            <th width="12%">Created At</th>
            <th width="10%">Actions</th>
          </tr>
        </thead>
        <tbody>
          {achProfiles.map((p, ix) => (
            <tr>
              <td>
                <Checkbox
                  disabled={p.status === "CANCELED" || p.status === "REJECTED"}
                  checked={
                    isWithdrawal
                      ? ix === selProfileWithIx
                      : ix === selProfileDepIx
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      isWithdrawal
                        ? setSelProfileWithIx(ix)
                        : setSelProfileDepIx(ix);
                    }
                  }}
                />
              </td>
              <td>{p.status}</td>
              <td>{p.nickname}</td>
              <td>{p.bankAccountType}</td>
              <td>{p.bankAccount}</td>
              <td>{parseObjectDate(new Date(p.createdAt))}</td>
              <td>{renderAchActions(p)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  useEffect(() => {
    fetchAch();
    getPlaidLink();
  }, []);

  return (
    <SkinPage>
      {loading && <Loader />}
      <div>
        <Widget name={"broker-connection"}>
          <div className="heading">
            <h1 className="title">Funding</h1>
          </div>
          <Accordion className="funding-accordion">
            <Accordion.Panel header="Deposit" defaultExpanded>
              <Tabs className="funding-tabs">
                <TabList>
                  <Tab>Wire</Tab>
                  <Tab>ACH</Tab>
                </TabList>
                <TabPanel>
                  <Accordion.Panel header="Domestic wires">
                    <p>
                      {" "}
                      Please follow the wiring instructions below and provide
                      this information to the sending financial institution:{" "}
                    </p>{" "}
                    <br />
                    <ul class="mx-0 mb-6 list-none px-0">
                      <li>
                        <strong>Account Name:</strong> Apex Clearing Inc.
                      </li>
                      <li>
                        <strong>ABA Routing:</strong> 071000288
                      </li>
                      <li>
                        <strong>Account:</strong> 3713286
                      </li>
                      <li>
                        <strong>Bank Name:</strong> BMO Bank N.A.
                      </li>
                      <li>
                        <strong>Address:</strong> 111 W. Monroe St, Chicago, IL
                        60603
                      </li>
                      <li>
                        <strong class="mr-1">Further Credit To:</strong>
                        <span data-automation="funding_deposit_wire_credit_to">
                          Francisco Flores / 6YA36366
                        </span>
                      </li>
                      <li class="text-sm text-gray-600">
                        <sup>*</sup> For all Corporate or Entity accounts please
                        only include the Entity name.{" "}
                      </li>
                    </ul>
                    <h5>Domestic FAQs</h5>
                    <ul class="list-disc">
                      <li>
                        <em>
                          {" "}
                          Wired funds must be sent from an account in the exact
                          same name of the Tradier Brokerage account holder. We
                          do not accept 3rd party wires.{" "}
                        </em>
                      </li>
                      <li>
                        <em>
                          {" "}
                          Wire deposits typically reflect in your account 1
                          business day after being sent.{" "}
                        </em>
                      </li>
                      <li>
                        <em>
                          {" "}
                          To expedite the processing of your wire deposit,
                          please email us a copy of your wire details or
                          receipt. Please do not request an expedite until at
                          least 4 business hours after your wire is sent, as
                          this is the minimum amount of time it takes to receive
                          your wire.{" "}
                        </em>
                      </li>
                    </ul>
                  </Accordion.Panel>
                  <Accordion.Panel header="International wires">
                    <p>
                      Please follow the wiring instructions below and provide
                      this information to the sending financial institution:
                    </p>
                    <br />
                    <ul class="mx-0 mb-6 list-none px-0">
                      <li>
                        <strong>Account Name:</strong> Apex Clearing Inc.
                      </li>
                      <li>
                        <strong>Swift Code:</strong> HATRUS44
                      </li>
                      <li>
                        <strong>Account:</strong> 3713286
                      </li>
                      <li>
                        <strong>Bank Name:</strong> BMO Bank N.A.
                      </li>
                      <li>
                        <strong>Address:</strong> 111 W. Monroe St, Chicago, IL
                        60603
                      </li>
                      <li>
                        <strong class="mr-1">Further Credit To:</strong>
                        <span data-automation="funding_deposit_wire_credit_to">
                          Francisco Flores / 6YA36366
                        </span>
                      </li>
                      <li class="text-sm text-gray-600">
                        <sup>*</sup> For all Corporate or Entity accounts please
                        only include the Entity name.{" "}
                      </li>
                    </ul>
                    <h5>International FAQs</h5>
                    <ul class="list-disc">
                      <li>
                        {" "}
                        Wired funds must be sent from an account in the exact
                        same name of the Tradier Brokerage account holder. We do
                        not accept 3rd party wires.{" "}
                      </li>
                      <li>
                        {" "}
                        International Wire deposits typically reflect in your
                        Tradier account within 2 business days after being sent.
                        If the deposit doesn't reflect in your account after 2
                        business days, please email us a copy of your wire
                        details or receipt so we may attempt to locate the
                        funds.{" "}
                      </li>
                      <li>
                        {" "}
                        Tradier Brokerage strongly recommends using an
                        international bank to send international wires. If using
                        an International Fund Transfer service (such as
                        Transferwise, Wise, or Revolut) please ensure they are
                        including your name as the Sender of the wire, the SWIFT
                        Code, and your name and Tradier account number in the
                        "Further Credit To" or notes/comments/additional
                        information section to ensure the wire is successfully
                        received. If they do not follow these instructions
                        explicitly, the wire may fail and any returned wire fees
                        will be your responsibility.{" "}
                      </li>
                    </ul>
                  </Accordion.Panel>
                </TabPanel>
                <TabPanel>
                  <h4>ACH Transfer</h4>
                  <p>
                    ACH Transfer Create an ACH profile for easy, frequent
                    deposits.
                  </p>
                  {achProfiles.length > 0 && (
                    <>
                      {renderAchTable()}
                      <table width={"50%"}>
                        <tr>
                          <td width={"50%"}>
                            <div className="form-field">
                              <TextInput
                                customInput={{
                                  value: amountDeposit,
                                  onChange: ({ target: { value } }) => {
                                    setAmountDeposit(value);
                                  },
                                  type: "number",
                                  placeholder: "Amount",
                                }}
                              />
                            </div>
                          </td>
                          <td width={"50%"}>
                            <div
                              onClick={handleDeposit}
                              style={{
                                borderRadius: "30px",
                                background: "inherit",
                                cursor: "pointer",
                                border: "1px solid #a1bca8",
                                padding: "0.75rem 1rem",
                                color: "#a1bca8",
                              }}
                              className="btn outline-btn"
                            >
                              Request deposit
                            </div>
                          </td>
                        </tr>
                      </table>
                    </>
                  )}
                  {accounts && (
                    <>
                      <p>
                        {" "}
                        ACH Transfers allow you to quickly move funds between
                        your Tradier Brokerage account and your bank account. To
                        link your bank account to your Tradier Brokerage account
                        via ACH, please click the Plaid button below.{" "}
                      </p>
                      <h4>Plaid</h4>
                      <p>
                        {" "}
                        Plaid is a new banking API system that helps fetch
                        information directly from your institution by logging
                        into your account online. Once logged in, we are able to
                        fetch the details of your account and verify ownership.{" "}
                      </p>
                      <ul class="mb-6">
                        <li>Up to 1 Business Day</li>
                        <li>
                          May require physical proof of the account to be
                          submitted
                        </li>
                        <li>Works with a limited number of banks*</li>
                        <li>Requires institution username and password</li>
                      </ul>
                      {accounts ? (
                        <>
                          <table className="basic-table">
                            <thead>
                              <tr>
                                <th colSpan={6}> Accounts</th>
                              </tr>
                              <tr>
                                <th width="10%"></th>
                                <th width="30%">Mask</th>
                                <th width="30%">Name</th>
                                <th width="30%">Type</th>
                              </tr>
                            </thead>
                            <tbody>
                              {accounts.accounts?.map((acc, ix) => (
                                <tr>
                                  <td>
                                    <Checkbox
                                      key={ix}
                                      checked={ix === selectedAccount}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedAccount(ix);
                                        } else {
                                          setSelectedAccount(null);
                                        }
                                      }}
                                    />
                                  </td>
                                  <td>{acc.mask}</td>
                                  <td>{acc.name}</td>
                                  <td>{acc.subtype}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <br />
                          <div>
                            <div
                              className="form-field"
                              style={{ width: "48%", display: "inline-block" }}
                            >
                              <TextInput
                                customInput={{
                                  type: "text",
                                  placeholder: "Nickname",
                                  value: accountNickname,
                                  onChange: (e) => {
                                    setaccountNickname(e.target.value);
                                  },
                                }}
                              />
                            </div>
                            <div
                              style={{ width: "48%", display: "inline-block" }}
                            >
                              <div
                                onClick={createAchProfile}
                                className="btn outline-btn"
                              >
                                Create ACH
                              </div>
                            </div>
                          </div>
                        </>
                      ) : null}
                    </>
                  )}

                  {plaidLink && (
                    <PlaidLink
                      className="btn outline-btn"
                      style={{
                        borderRadius: "30px",
                        background: "inherit",
                        cursor: "pointer",
                        border: "1px solid #a1bca8",
                        padding: "0.75rem 1rem",
                        color: "#a1bca8",
                      }}
                      token={plaidLink}
                      onSuccess={onPlaidSuccess}
                      onExit={onPlaidExit}
                    >
                      Setup Plaid
                    </PlaidLink>
                  )}
                </TabPanel>
              </Tabs>
            </Accordion.Panel>
            <Accordion.Panel header="Withdrawal">
              <Tabs className="funding-tabs">
                <TabList>
                  {/* <Tab>Wire</Tab> */}
                  <Tab>ACH</Tab>
                </TabList>
                {/* <TabPanel>PENDING STUFF</TabPanel> */}
                <TabPanel>
                  {renderAchTable(true)}
                  <br />
                  <h4>ACH Withdrawal</h4>
                  <p>
                    May take up to 2 to 3 business days to post to your bank
                    account. Please review fund availability rules regarding
                    withdrawal restrictions.
                  </p>
                  <br />
                  <table width={"50%"}>
                    <tr>
                      <td width={"50%"}>
                        <div className="form-field">
                          <TextInput
                            customInput={{
                              value: amountWithdrawal,
                              onChange: ({ target: { value } }) => {
                                setAmountWithdrawal(value);
                              },
                              type: "number",
                              placeholder: "Amount",
                            }}
                          />
                        </div>
                      </td>
                      <td width={"50%"}>
                        <div
                          onClick={handleWithdrawal}
                          style={{
                            borderRadius: "30px",
                            background: "inherit",
                            cursor: "pointer",
                            border: "1px solid #a1bca8",
                            padding: "0.75rem 1rem",
                            color: "#a1bca8",
                          }}
                          className="btn outline-btn"
                        >
                          Request Withdrawal
                        </div>
                      </td>
                    </tr>
                  </table>
                </TabPanel>
              </Tabs>
            </Accordion.Panel>
          </Accordion>
        </Widget>
        <DgModal
          modalState={openModalVerif}
          handleModalState={() => {
            setOpenModalVerif(!openModalVerif);
          }}
        >
          <h1 className="title">Verify ach profile</h1>
          <h3 style={{ color: "#95b2c5" }}>{currVerifyAch.nickname}</h3>
          <div className="strategy-content">
            <div className="form-field">
              <TextInput
                customInput={{
                  type: "number",
                  min: 0.01,
                  label: "Amount 1",
                  name: "amount1",
                  tooltipBool: false,
                  value: selectedAch.amount1,
                  action: setVerifyData,
                }}
              />
            </div>
            <div className="form-field">
              <TextInput
                customInput={{
                  type: "number",
                  min: 0.01,
                  label: "Amount 2",
                  name: "amount2",
                  tooltipBool: false,
                  value: selectedAch.amount2,
                  action: setVerifyData,
                }}
              />
            </div>
            <DgButton
              category={"primary"}
              size={"small"}
              text={"Verify ACH"}
              action={handleVerifylAch}
            ></DgButton>
          </div>
        </DgModal>
      </div>
    </SkinPage>
  );
}

export default Funding;
